// 분배금 등록 화면 (tb_dividend)
// 계좌·연도·기준일자 조회 + 종목별 월별 분배금 입력 그리드
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchDividendList, saveDividends } from '../../api/dividend';
import { fetchAccounts } from '../../api/accounts';
import PageLayout from '../../components/PageLayout';
import StockChartPopup from '../../components/common/StockChartPopup';
import './DividendPage.css';

const MONTHS = [1,2,3,4,5,6,7,8,9,10,11,12];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = ['전체', ...Array.from({ length: 5 }, (_, i) => String(currentYear - i))];

const defaultBaseDate = (() => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 = last day of prev month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

const fNum = (v) =>
  v != null && v !== '' && Number(v) !== 0
    ? Number(v).toLocaleString('ko-KR')
    : '';

const isInterest = (row) =>
  row?.stock_type === 'DEPOSIT' || row?.stock_type === 'CMA';

const cKey = (row, m) => `${row.account_name}|||${row.ticker}|||${m}`;
const rKey = (row) => `${row.account_name}|||${row.ticker}`;
const iKey = (row) => `invest|||${rKey(row)}`;

const ACCT_COL_W = 90;

const DividendPage = ({ isActive }) => {
  const [divYear,       setDivYear]       = useState(String(currentYear));
  const [filterAccount, setFilterAccount] = useState('');
  const [baseDate,      setBaseDate]      = useState(defaultBaseDate);
  const [accounts,      setAccounts]      = useState([]);
  const [rows,          setRows]          = useState([]);
  const [editMap,       setEditMap]       = useState({});
  const [investEditMap, setInvestEditMap] = useState({});
  const [activeKey,     setActiveKey]     = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [message,       setMessage]       = useState({ text: '', type: '' });
  const [sortConfig,    setSortConfig]    = useState(null); // { key, dir }
  const [stockNameWidth, setStockNameWidth] = useState(200);
  const [chartPopup,    setChartPopup]    = useState(null);
  const resizingRef = useRef(false);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  useEffect(() => {
    if (!isActive) return;
    fetchAccounts().then(setAccounts).catch(() => {});
  }, [isActive]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (divYear !== '전체') params.year = parseInt(divYear, 10);
      if (filterAccount) params.account_name = filterAccount;
      if (baseDate) params.base_date = baseDate;
      const res = await fetchDividendList(params);
      setRows(res.data || []);
      setEditMap({});
      setInvestEditMap({});
      setActiveKey(null);
    } catch {
      showMessage('조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [divYear, filterAccount, baseDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Sort ─────────────────────────────────────────────────────

  const getSubtotal = useCallback((row) => {
    let sum = 0;
    for (const m of MONTHS) {
      const key = cKey(row, m);
      const val = key in editMap ? editMap[key] : row[`m${m}`];
      const n = parseFloat(val);
      if (!isNaN(n) && n > 0) sum += n;
    }
    return sum;
  }, [editMap]);

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev?.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: 'asc' };
    });
  };

  const sortArrow = (key) => {
    if (sortConfig?.key !== key) return ' ↕';
    return sortConfig.dir === 'asc' ? ' ↑' : ' ↓';
  };

  const displayRows = useMemo(() => {
    if (!sortConfig) return rows;
    const { key, dir } = sortConfig;
    return [...rows].sort((a, b) => {
      let va, vb;
      if (key === 'subtotal') {
        va = getSubtotal(a);
        vb = getSubtotal(b);
      } else if (key === 'stock_name') {
        va = (a.stock_name || a.ticker || '').toLowerCase();
        vb = (b.stock_name || b.ticker || '').toLowerCase();
      } else {
        va = (a[key] || '').toLowerCase();
        vb = (b[key] || '').toLowerCase();
      }
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortConfig, getSubtotal]);

  const grandTotal = useMemo(
    () => displayRows.reduce((sum, row) => sum + getSubtotal(row), 0),
    [displayRows, getSubtotal],
  );

  // ── Column resize ─────────────────────────────────────────────

  const handleStockResizeStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = stockNameWidth;
    resizingRef.current = true;
    const onMove = (ev) => {
      if (!resizingRef.current) return;
      setStockNameWidth(Math.max(80, startW + ev.clientX - startX));
    };
    const onUp = () => {
      resizingRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [stockNameWidth]);

  // ── Cell value helpers ────────────────────────────────────────

  const isDirty = (row, m) => cKey(row, m) in editMap;
  const isInvestDirty = (row) => isInterest(row) && rKey(row) in investEditMap;

  const getCellValue = (row, m) => {
    const key = cKey(row, m);
    if (key in editMap) {
      const raw = editMap[key];
      if (activeKey === key) return raw;
      const n = parseFloat(raw);
      return !isNaN(n) && n > 0 ? fNum(n) : '';
    }
    const v = row[`m${m}`];
    if (v == null || v <= 0) return '';
    return activeKey === key ? String(v) : fNum(v);
  };

  const getInvestValue = (row) => {
    const key = iKey(row);
    if (rKey(row) in investEditMap) {
      const raw = investEditMap[rKey(row)];
      if (activeKey === key) return raw;
      const n = parseFloat(raw);
      return !isNaN(n) && n > 0 ? fNum(n) : '';
    }
    const v = row.invest_amount;
    if (!v || v <= 0) return '';
    return activeKey === key ? String(v) : fNum(v);
  };

  // ── Event handlers ────────────────────────────────────────────

  const handleCellChange = (row, m, val) => {
    const cleaned = val.replace(/[^0-9.]/g, '');
    setEditMap(prev => ({ ...prev, [cKey(row, m)]: cleaned }));
  };

  const handleInvestChange = (row, val) => {
    const cleaned = val.replace(/[^0-9.]/g, '');
    setInvestEditMap(prev => ({ ...prev, [rKey(row)]: cleaned }));
  };

  const handleSave = async () => {
    if (divYear === '전체') {
      showMessage('저장 시 특정 연도를 선택해야 합니다.', 'error');
      return;
    }
    const year = parseInt(divYear, 10);
    const items = Object.entries(editMap).map(([key, rawVal]) => {
      const [account_name, ticker, monthStr] = key.split('|||');
      const row = rows.find(r => r.account_name === account_name && r.ticker === ticker);
      const rowKey = `${account_name}|||${ticker}`;
      const invest_amount =
        isInterest(row) && rowKey in investEditMap
          ? (parseFloat(investEditMap[rowKey]) || 0)
          : (row?.invest_amount || 0);
      return {
        account_name,
        ticker,
        month:         parseInt(monthStr, 10),
        amount:        rawVal === '' ? 0 : (parseFloat(rawVal) || 0),
        invest_amount,
      };
    });

    if (items.length === 0) {
      showMessage('변경된 분배금 데이터가 없습니다.', 'info');
      return;
    }

    setLoading(true);
    try {
      const res = await saveDividends({ year, items });
      showMessage(res.message || '저장되었습니다.');
      await loadData();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const hasDirtyData = Object.keys(editMap).length > 0;
  const canSave = hasDirtyData && divYear !== '전체';
  const c3Left = ACCT_COL_W + stockNameWidth;

  // ── Render ────────────────────────────────────────────────────

  const toolbar = (
    <div className="div-toolbar">
      <label className="div-toolbar-label">계좌</label>
      <select
        className="div-acct-select"
        value={filterAccount}
        onChange={(e) => setFilterAccount(e.target.value)}
      >
        <option value="">전체</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.account_name}>{a.account_name}</option>
        ))}
      </select>

      <label className="div-toolbar-label">연도</label>
      <select
        className="div-year-select"
        value={divYear}
        onChange={(e) => setDivYear(e.target.value)}
      >
        {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
      </select>

      <label className="div-toolbar-label">투자잔액 기준일자</label>
      <input
        type="date"
        className="div-date-input"
        value={baseDate}
        onChange={(e) => setBaseDate(e.target.value)}
      />

      <button className="btn btn-primary" onClick={loadData} disabled={loading}>
        조회
      </button>
      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={loading || !canSave}
      >
        저장
      </button>

      {grandTotal > 0 && (
        <span className="div-summary-text">
          분배금 합계: <strong>{fNum(grandTotal)}</strong>
        </span>
      )}
    </div>
  );

  return (
    <>
      <PageLayout title="분배금 등록" toolbar={toolbar} message={message}>
        <div className="grid-wrapper">
          <table className="grid-table div-grid">
            <thead>
              <tr>
                <th
                  className="col-freeze freeze-c1 div-sortable"
                  style={{ width: ACCT_COL_W }}
                  onClick={() => handleSort('account_name')}
                >
                  계좌명{sortArrow('account_name')}
                </th>
                <th
                  className="col-freeze freeze-c2 div-sortable"
                  style={{ width: stockNameWidth, left: ACCT_COL_W }}
                  onClick={() => handleSort('stock_name')}
                >
                  종목명{sortArrow('stock_name')}
                  <span
                    className="col-resize-handle"
                    onMouseDown={(e) => { e.stopPropagation(); handleStockResizeStart(e); }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th
                  className="col-freeze freeze-c3 col-center div-sortable"
                  style={{ width: 65, left: c3Left }}
                  onClick={() => handleSort('ticker')}
                >
                  티커{sortArrow('ticker')}
                </th>
                <th className="col-right" style={{ width: 100 }}>투자잔액</th>
                <th
                  className="col-right div-sortable"
                  style={{ width: 85 }}
                  onClick={() => handleSort('subtotal')}
                >
                  소계{sortArrow('subtotal')}
                </th>
                {MONTHS.map((m) => (
                  <th key={m} className="col-right" style={{ width: 65 }}>{m}월</th>
                ))}
                <th style={{ width: 150 }}>메모</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr>
                  <td colSpan={18} className="grid-empty">
                    {loading ? '조회 중...' : '데이터가 없습니다.'}
                  </td>
                </tr>
              ) : (
                displayRows.map((row, idx) => {
                  const interest    = isInterest(row);
                  const subtotal    = getSubtotal(row);
                  const investDirty = isInvestDirty(row);

                  return (
                    <tr
                      key={`${row.account_name}__${row.ticker}__${idx}`}
                      className={interest ? 'row-deposit' : ''}
                    >
                      <td
                        className="col-freeze freeze-c1"
                        title={interest ? '투자잔액(원금)을 먼저 수정 후 이자금액을 입력하세요' : undefined}
                      >
                        {row.account_name}
                        {interest && (
                          <span
                            className="deposit-tip"
                            title="투자잔액(원금)을 먼저 수정 후 이자금액을 입력하세요"
                          >
                            💡
                          </span>
                        )}
                      </td>

                      <td
                        className="col-freeze freeze-c2 col-ellipsis"
                        style={{ left: ACCT_COL_W }}
                        title={row.stock_name || row.ticker}
                      >
                        <span
                          className="stock-chart-link"
                          onClick={() => setChartPopup({
                            ticker: row.ticker,
                            stockName: row.stock_name || row.ticker,
                            accountName: row.account_name,
                            turns: [],
                          })}
                        >
                          {row.stock_name || row.ticker}
                        </span>
                      </td>

                      <td
                        className="col-freeze freeze-c3 col-center"
                        style={{ left: c3Left }}
                      >
                        {row.ticker}
                      </td>

                      {interest ? (
                        <td className={`div-month-cell${investDirty ? ' cell-dirty' : ''}`}>
                          <input
                            type="text"
                            className="month-input"
                            value={getInvestValue(row)}
                            placeholder="원금 입력"
                            onFocus={() => setActiveKey(iKey(row))}
                            onChange={(e) => handleInvestChange(row, e.target.value)}
                            onBlur={() => setActiveKey(null)}
                          />
                        </td>
                      ) : (
                        <td className="col-right div-readonly">
                          {row.invest_amount > 0 ? fNum(row.invest_amount) : ''}
                        </td>
                      )}

                      <td className="col-right div-subtotal">
                        {subtotal > 0 ? fNum(subtotal) : ''}
                      </td>

                      {MONTHS.map((m) => {
                        const key   = cKey(row, m);
                        const dirty = isDirty(row, m);
                        return (
                          <td key={m} className={`div-month-cell${dirty ? ' cell-dirty' : ''}`}>
                            <input
                              type="text"
                              className="month-input"
                              value={getCellValue(row, m)}
                              onFocus={() => setActiveKey(key)}
                              onChange={(e) => handleCellChange(row, m, e.target.value)}
                              onBlur={() => setActiveKey(null)}
                            />
                          </td>
                        );
                      })}

                      <td className="div-memo col-ellipsis" title={row.invest_memo || ''}>
                        {row.invest_memo || ''}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </PageLayout>

      {chartPopup && (
        <StockChartPopup
          ticker={chartPopup.ticker}
          stockName={chartPopup.stockName}
          accountName={chartPopup.accountName}
          turns={chartPopup.turns || []}
          onClose={() => setChartPopup(null)}
        />
      )}
    </>
  );
};

export default DividendPage;
